import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { X, ImagePlus, Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["Athlete Stories", "Training", "Nutrition", "Recovery", "Gear", "Expeditions"];

const inputCls =
  "w-full bg-[#FBF9F5]/60 border border-[#0D1117]/15 rounded-xl px-5 py-3.5 text-sm text-[#0D1117] placeholder:text-[#6B7280] focus:border-[#FF4E00]/60 focus:outline-none transition-all duration-300";

export default function StorySubmit({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", title: "", category: "Athlete Stories", story: "" });
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10 MB");
      return;
    }
    setImage(f);
    setPreview(URL.createObjectURL(f));
  };

  const reset = () => {
    setForm({ name: "", email: "", title: "", category: "Athlete Stories", story: "" });
    setImage(null);
    setPreview("");
    setProgress(0);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setProgress(0);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (image) fd.append("image", image);
      await axios.post(`${API}/stories`, fd, {
        onUploadProgress: (p) => setProgress(p.total ? Math.round((p.loaded / p.total) * 100) : 0),
      });
      toast.success("Story received — our editors will review it for the Journal.");
      reset();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit your story — please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-[#0D1117]/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
          onClick={onClose}
          data-testid="story-submit-modal"
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#FBF9F5] rounded-3xl max-w-xl w-full p-7 sm:p-9 relative max-h-[90vh] overflow-y-auto"
          >
            <button onClick={onClose} className="absolute top-5 right-5 text-[#6B7280] hover:text-[#FF4E00]" aria-label="Close" data-testid="story-submit-close">
              <X className="w-5 h-5" />
            </button>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00]">Write For The Journal</p>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#0D1117] mt-3">Share your story with the tribe.</h2>
            <p className="text-sm text-[#374151] mt-2 leading-relaxed">
              Training breakthroughs, race-day lessons, recovery wins — if it moved you, it will move someone else.
            </p>
            <form onSubmit={submit} className="mt-7 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="story-input-name" />
                <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="story-input-email" />
              </div>
              <input required placeholder="Story title — e.g. My first 10K at 50" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="story-input-title" />
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={`${inputCls} appearance-none cursor-pointer`} data-testid="story-select-category">
                {CATEGORIES.map((c) => (
                  <option key={c} value={c} className="bg-[#FFFFFF]">{c}</option>
                ))}
              </select>
              <textarea required rows={5} placeholder="Tell it like you'd tell a running buddy..." value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })} className={`${inputCls} resize-none`} data-testid="story-textarea" />
              <div>
                <input ref={fileRef} type="file" accept="image/*" onChange={pick} className="hidden" data-testid="story-file-input" />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  data-testid="story-image-button"
                  className="w-full inline-flex items-center justify-center gap-2 border border-dashed border-[#FF4E00]/40 hover:border-[#FF4E00] text-[#FF4E00] font-mono text-[11px] uppercase tracking-[0.2em] px-6 py-4 rounded-xl transition-colors duration-300"
                >
                  <ImagePlus className="w-4 h-4" />
                  {image ? "Change photo" : "Add a photo (optional, max 10 MB)"}
                </button>
                {preview && (
                  <div className="mt-3 rounded-xl overflow-hidden border border-[#0D1117]/10">
                    <img src={preview} alt="Story upload preview" className="w-full max-h-48 object-cover" data-testid="story-image-preview" />
                  </div>
                )}
              </div>
              {busy && (
                <div className="h-1.5 rounded-full bg-[#E7E4DA] overflow-hidden" data-testid="story-upload-progress">
                  <div className="h-full bg-[#FF4E00] transition-all duration-300" style={{ width: `${progress}%` }} />
                </div>
              )}
              <button
                type="submit"
                disabled={busy}
                data-testid="story-submit-button"
                className="w-full inline-flex items-center justify-center gap-3 bg-[#FF4E00] hover:bg-[#E04000] disabled:opacity-60 text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.25em] px-8 py-4 rounded-full transition-all duration-300"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {busy ? `Sending${progress ? ` ${progress}%` : "..."}` : "Submit Story"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
